import { EventEmitter, inject, Injectable, ModuleWithProviders } from '@angular/core';
import { BehaviorSubject, Observable, Subject, merge } from 'rxjs';
import { Layer, LayerList, LayerType } from '../editor/models/layer.model';
import { BaseData, CachedCalculatedValues, StringEncodings, WorkerLayerData, WorkerResponse } from './image-draw.model';
import objectHash from 'object-hash';
import { EditorState } from '../editor/store/editor.reducer';
import { EffectsType } from '../editor/models/ui-effects.model';
import { getSVGPattern } from '../editor/models/patterns.svg.model';
import { FirebaseRenderData } from './firebase.model';
import 'context-filter-polyfill';
import { ChannelPosition, ImageChannelData } from '../file-reader/v1/file-reading.model';
import pica from 'pica';
import JSZip from 'jszip';
import { Option } from '../editor/models/properties.model';
import { generateBuildingOptions, generateRoadOptions } from './image-data.utils';
import { RawImageDataLoaderService } from './image-loader.service';

export const THUMBNAIL_SIZE = 150
export const BIG_THUMBNAIL_SIZE = 300

export let CURRENT_STRING_ENCODINGS: StringEncodings | undefined // This is definitly a bad idea

interface OutputLayer {
  name: string
  type: LayerType | string
  dataUrl: string
}

@Injectable({
  providedIn: 'root',
})
export class ImageDrawService {
  private imageData = inject(RawImageDataLoaderService)

  public pushImageData = new EventEmitter<EditorState>()
  public updateLayerQuickly = new EventEmitter<Layer>()

  private updater$ = new BehaviorSubject<boolean>(true)
  public previewImageUpdateStream$ = this.updater$.asObservable()

  private canvasCache = new Map<string, Uint8ClampedArray>()
  private layerHash = new Map<string, string>()
  private workerPool: Worker[] = []

  private _lastEditorState: EditorState

  constructor() {
    this.updateLayerQuickly.subscribe(layer => {
      this._lastEditorState.layers[layer.id] = layer
      this.forceRedraw()
      this.updater$.next(true)
    })
    merge(this.pushImageData.asObservable()).subscribe(editorState => {
      this._lastEditorState = editorState
      console.log(editorState)
      this.updater$.next(true)
    })
  }

  public forceRedraw() {
    this.updater$.next(true)
  }

  private getOffScreenCanvas(data: BaseData): OffscreenCanvas {
    const canvas = document.createElement('canvas')
    canvas.width = data.headers.imageSize.width
    canvas.height = data.headers.imageSize.height
    return canvas.transferControlToOffscreen()
  }


  private async getSVGUrls(layer: Layer, isScaledCanvas: boolean): Promise<ImageData | undefined>{
    const layerHasTexture = layer.effects.some(effect => effect.effectType === EffectsType.Texture)
    if (!layerHasTexture) {
      return
    }

    let imgWidth: number
    if (isScaledCanvas) {
      imgWidth = this.imageData.getImageSize('preview').width
    }
    else {
      imgWidth = this.imageData.getImageSize('full').width
    }
    const assumedSvgWidth = 20
    let textureScale = (layer.effectSettings.textureSettings.textureScale * imgWidth * .2) / assumedSvgWidth


    const svg = getSVGPattern(
      layer.effectSettings.textureSettings.textureSelector.value,
      layer.effectSettings.textureSettings.textureColor,
      layer.effectSettings.textureSettings.textureOpacity,
      textureScale
    )

    const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.src = url;

    await new Promise<void>((resolve) => {
      img.onload = function() {
        resolve()
      }
    })

    var canvas = document.createElement('canvas');
    canvas.width = img.width;
    canvas.height = img.height;
    var context = canvas.getContext('2d');
    if (!context){
      return
    }
    if (canvas.width === 0 || canvas.height === 0) {
      return
    }
    context.drawImage(img, 0, 0 );
    const imageData = context.getImageData(0, 0, img.width, img.height);
    return imageData
  };

  public static forRoot(): ModuleWithProviders<ImageDrawService> {
    return {
      ngModule: ImageDrawService,
      providers: [ImageDrawService]
    };
  }

  private whitelistDataSets(whiteListed: ChannelPosition[], channelData: ImageChannelData): ImageChannelData {
    return channelData.map((channel, idx) => {
      if (whiteListed.includes(idx)){
        return channel
      }
      else {
        return new ArrayBuffer(0);
      }
    })
  }

  private parDownCachedDatasets(layerType: LayerType, cached: CachedCalculatedValues): CachedCalculatedValues{
    const emptyCachedValues: CachedCalculatedValues = {
      ...cached,
      sepeatedBuildings: new Uint16Array()
    }
    switch(layerType) {
      case LayerType.Base: {
        return emptyCachedValues
      }
      case LayerType.Overlay: {
        return emptyCachedValues
      }
      case LayerType.POIGlow: {
        return {
          ...emptyCachedValues,
          interestMap: cached.interestMap
        }
      }
      case LayerType.GridLines: {
        return emptyCachedValues
      }
      case LayerType.Buildings: {
        return {
          ...emptyCachedValues,
          sepeatedBuildings: cached.sepeatedBuildings
        }
      }
      case LayerType.Contour: {
        return emptyCachedValues
      }
      case LayerType.Water: {
        return emptyCachedValues
      }
      case LayerType.Roads: {
        return emptyCachedValues
      }
      case LayerType.MaterialOverlay: {
        return emptyCachedValues
      }
      case LayerType.Zones: {
        return {
          ...emptyCachedValues,
          sepeatedBuildings: cached.sepeatedBuildings
        }
      }
      default:
        return emptyCachedValues
    }
  }

  private parDownRequiredDatasets(layerType: LayerType, channelData: ImageChannelData): ImageChannelData{
    switch(layerType) {
      case LayerType.Base: {
        return this.whitelistDataSets(
          [
            ChannelPosition.R,
            ChannelPosition.G,
            ChannelPosition.B,
          ],
          channelData
        )
      }
      case LayerType.Overlay: {
        return this.whitelistDataSets(
          [
          ],
          channelData
        )
      }
      case LayerType.POIGlow: {
        return this.whitelistDataSets(
          [
            ChannelPosition.Buildings,
            ChannelPosition.Roads
          ],
          channelData
        )
      }
      case LayerType.GridLines: {
        return this.whitelistDataSets(
          [
          ],
          channelData
        )
      }
      case LayerType.Buildings: {
        return this.whitelistDataSets(
          [
            ChannelPosition.Buildings,
          ],
          channelData
        )
      }
      case LayerType.Contour: {
        return this.whitelistDataSets(
          [
            ChannelPosition.Height,
            ChannelPosition.Buildings,
            ChannelPosition.Roads,
          ],
          channelData
        )
      }
      case LayerType.Water: {
        return this.whitelistDataSets(
          [
            ChannelPosition.Water,
            ChannelPosition.Height
          ],
          channelData
        )
      }
      case LayerType.Roads: {
        return this.whitelistDataSets(
          [
            ChannelPosition.Roads,
          ],
          channelData
        )
      }
      case LayerType.MaterialOverlay: {
        return this.whitelistDataSets(
          [
            ChannelPosition.Material,
          ],
          channelData
        )
      }
      default:
        return channelData
    }
  }

  public async updatePreviewImage(
    canvas: HTMLCanvasElement
  ) {
    await this.updateCanvas(
      canvas,
      this._lastEditorState.layers,
      this._lastEditorState.layerOrder
    )
  }

  public async updateCanvas(
    canvas: HTMLCanvasElement | OffscreenCanvas,
    layers: LayerList,
    layerOrder: string[],
    renderData: BaseData = this.imageData.getImageData('preview'),
    disableCache: boolean = false,
    outputLayers?: OutputLayer[]
  ): Promise<void> {
    if (!canvas.getContext || !renderData) {
      return
    }
    const layerCalculated: Promise<WorkerResponse>[] = []

    const isScaledDown = renderData.headers.imageSize.width !== this.imageData.getImageSize('full').width

    layerOrder.forEach((layerId)  => {
      layerCalculated.push(new Promise(async (resolve, reject) => {
        const cachedLayer = this.canvasCache.get(layerId)
        const workerMessage: WorkerLayerData = {
          base: {
            ...renderData,
            imageData: this.parDownRequiredDatasets(
              layers[layerId].layerType,
              renderData.imageData
            ),
            cachedCalculatedValues: this.parDownCachedDatasets(
              layers[layerId].layerType,
              renderData.cachedCalculatedValues
            )
          },
          layer: layers[layerId],
          lastLayerHash: this.layerHash.get(layerId),
          cachedLayer,
          offScreenCanvas: this.getOffScreenCanvas(renderData),
          texturePattern: await this.getSVGUrls(layers[layerId], isScaledDown),
          disableCache,
          originalImageSize: this.imageData.getImageSize('full')
        }
        const worker = this.getOrCreateLayerWorker()
        worker.onmessage = (event: MessageEvent<WorkerResponse>) => {
          if (event.data.layerHash === objectHash(workerMessage.layer) || disableCache) {
            resolve(event.data)
            worker.onmessage = null
            this.workerPool.push(worker)
          }
        }
        worker.onerror = (error) => {
          reject(workerMessage.layer.name)
          console.error(error)
        }
        worker.postMessage(workerMessage, [workerMessage.offScreenCanvas as OffscreenCanvas])
      }))
    })

    const calculatedLayers = await Promise.all(layerCalculated)

    const map = new Map<string, Uint8ClampedArray>()
    calculatedLayers.forEach((layerData, idx) => {
      if (!layers[layerOrder[idx]].sharedSettings.visible){
        return
      }
      map.set(layerOrder[idx], layerData.canvas)
      this.canvasCache.set(layerOrder[idx], layerData.canvas)
      this.layerHash.set(layerOrder[idx], layerData.layerHash)
    })
    this.drawLayer(canvas, layers, layerOrder, map, renderData, outputLayers)
  }

  public getCurrentStringEncodings(): StringEncodings {
    return this.imageData.getStringEncodings()
  }

  private getOrCreateLayerWorker(): Worker {
    let worker = this.workerPool.pop()
    if (!worker) {
      worker = new Worker(new URL('./layer-calculations.worker', import.meta.url))
    }
    return worker
  }

  private drawLayer(canvas: HTMLCanvasElement | OffscreenCanvas, layers: LayerList, layerOrder: string[], canvasData: Map<string,Uint8ClampedArray>, renderData: BaseData, outputLayers?: OutputLayer[]): void {
    const primaryContext = canvas.getContext('2d') as CanvasRenderingContext2D

    primaryContext.clearRect(0, 0, canvas.width, canvas.height);

    const imageSize = renderData.headers.imageSize

    canvas.width = imageSize.width
    canvas.height = imageSize.height


    layerOrder.reverse().forEach(layerId => {
      const tempCanvas = document.createElement('canvas')
      tempCanvas.width = imageSize.width
      tempCanvas.height = imageSize.height
      const context = tempCanvas.getContext('2d') as CanvasRenderingContext2D
      const imageData = canvasData.get(layerId)
      if (imageData) {
        context.putImageData(new ImageData(imageData, imageSize.width),0,0,0,0, canvas.width, canvas.height)

        let filterCanvas = document.createElement('canvas')
        filterCanvas.width = imageSize.width
        filterCanvas.height = imageSize.height

        const filterContext = filterCanvas.getContext('2d') as CanvasRenderingContext2D

        filterContext.filter = `opacity(${Math.floor(layers[layerId].sharedSettings.opacity * 100)}%)`;

        filterContext.drawImage(tempCanvas, 0, 0, imageSize.width, imageSize.height, 0, 0, canvas.width, canvas.height)

        if (outputLayers) {
          outputLayers.push({
            name: layers[layerId].name,
            type: layers[layerId].layerType,
            dataUrl: filterCanvas.toDataURL("image/png")
          })
        }
        primaryContext.globalCompositeOperation = layers[layerId].sharedSettings.blendingMode.value
        primaryContext.drawImage(filterCanvas, 0, 0, imageSize.width, imageSize.height, 0, 0, canvas.width, canvas.height)
      }
    })
  }

  public async exportRenderFullSize(layerData: FirebaseRenderData['editorState']){
    const fullScaleSize = this.imageData.getImageSize('full')

    const fullCanvas = document.createElement('canvas')
    fullCanvas.height = fullScaleSize.height
    fullCanvas.width = fullScaleSize.width

    if (!layerData) {
      throw new Error('No layer data')
    }
    await this.updateCanvas(
      fullCanvas,
      layerData.layers,
      layerData?.layerOrder,
      this.imageData.getImageData('full'),
      true
    )
    return fullCanvas.toDataURL("image/png")
  }

  public async exportRenderZipped(layerData: FirebaseRenderData['editorState']){
    const fullScaleSize = this.imageData.getImageSize('full')

    const fullCanvas = document.createElement('canvas')
    fullCanvas.height = fullScaleSize.height
    fullCanvas.width = fullScaleSize.width

    if (!layerData) {
      throw new Error('No layer data')
    }

    const zip = new JSZip()

    const layers: OutputLayer[] = []

    await this.updateCanvas(
      fullCanvas,
      layerData.layers,
      layerData?.layerOrder,
      this.imageData.getImageData('full'),
      true,
      layers
    )

    layers.push({
      name: "combined",
      type: "output",
      dataUrl: fullCanvas.toDataURL("image/png")
    })

    layers.forEach(layer => {
      zip.file(
        `${layer.name.replace(" ", "-")}_${layer.type}.png`,
        layer.dataUrl.replace(/^data:image\/png;base64,/, ''),
        { base64: true }
      );
    })

    return await zip.generateAsync({ type: 'blob' })
  }


  public async createThumbnailImage(layerData: FirebaseRenderData['editorState'], bigImage: boolean = false): Promise<string> {
    const imageData = this.imageData.getImageData('preview')
    const fullCanvas = document.createElement('canvas')
    fullCanvas.height = imageData.headers.imageSize.height
    fullCanvas.width = imageData.headers.imageSize.width
    if (!layerData) {
      throw new Error('No layer data')
    }

    await this.updateCanvas(
      fullCanvas,
      layerData.layers,
      layerData?.layerOrder,
      this.imageData.getImageData('preview'),
      true
    )

    const aspectRatio = fullCanvas.width / fullCanvas.height
    const tempCanvas = document.createElement('canvas')
    const imageSize = bigImage ? BIG_THUMBNAIL_SIZE : THUMBNAIL_SIZE
    tempCanvas.height = imageSize
    tempCanvas.width = imageSize * aspectRatio

    const scaler = pica()
    await scaler.resize(fullCanvas, tempCanvas)

    return tempCanvas.toDataURL("image/jpeg")
  }
}
